import {
  ChangeDetectionStrategy,
  Component,
  computed,
  OnInit,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { OrderThankYouCardComponent } from '../../components/order-thank-you-card/order-thank-you-card.component';
import { OrderCartItemComponent } from '../../components/order-cart-item/order-cart-item.component';
import { OrderPricesCardComponent } from '../../components/order-prices-card/order-prices-card.component';
import { ActivatedRoute, Router } from '@angular/router';
import { WooCommerceCheckoutResponse } from '../../../../core/interfaces/woo-commerce/api/checkout/checkout-api.interface';
import { OrdersState } from '../../../../shared/state/orders/orders.state';
import { Store } from '@ngxs/store';
import { WooCommerceDatetimeTimeSlotValue } from '../../../../core/enums/woo-commerce/api/woo-commerce-api.enum';
import { DatePipe } from '@angular/common';
import { ShippingMethod } from '../../../../core/enums/woo-commerce/shipping/shipping.enum';

@Component({
  selector: 'app-order-success-page',
  imports: [
    OrderThankYouCardComponent,
    OrderCartItemComponent,
    OrderPricesCardComponent
  ],
  templateUrl: './order-success-page.component.html',
  styleUrl: './order-success-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe]
})
export class OrderSuccessPageComponent implements OnInit {
  protected readonly Boolean = Boolean;

  orderDetails: WritableSignal<WooCommerceCheckoutResponse> = signal(
    {} as WooCommerceCheckoutResponse
  );

  subtotal: Signal<number> = computed(() => {
    return this.orderDetails().products.reduce((acc, product) => {
      return acc + this.toNumber(product.subtotal);
    }, 0);
  });

  deliveryDate: Signal<string> = computed((): string => {
    const orderDetails: WooCommerceCheckoutResponse = this.orderDetails();

    const deliveryDateValue: string = this.getDeliveryDateValue(orderDetails);
    if (!deliveryDateValue) {
      return this.formatDate2DeliveryDateLabel();
    }

    const isAsap: boolean = deliveryDateValue
      .toLowerCase()
      .includes(WooCommerceDatetimeTimeSlotValue.Asap.toLowerCase());

    if (isAsap) {
      return deliveryDateValue;
    }

    // ISO timestamp from the new backend (e.g. "2026-05-06T18:05:00.000000Z")
    if (this.isIsoTimestamp(deliveryDateValue)) {
      return this.formatIsoTimestamp(deliveryDateValue);
    }

    const splitDate: string[] = deliveryDateValue.split(' - ');
    const deliveryDate: Date = new Date(splitDate[0]);

    if (isNaN(deliveryDate.getTime())) {
      return deliveryDateValue;
    }

    return splitDate[1]
      ? deliveryDateValue
      : this.formatDate2DeliveryDateLabel(deliveryDateValue);
  });

  isPickup: Signal<boolean> = computed((): boolean => {
    const orderDetails: WooCommerceCheckoutResponse = this.orderDetails();

    if (orderDetails?.fulfillment_type) {
      return orderDetails.fulfillment_type.toLowerCase() === ShippingMethod.Pickup.toLowerCase();
    }

    if (!orderDetails?.totals?.shipping_lines?.length) {
      return false;
    }

    const shippingLine = orderDetails.totals.shipping_lines[0];
    const shippingMethodId: string = shippingLine.method_id?.toLowerCase() || '';
    const shippingMethodTitle: string = shippingLine.method_title?.toLowerCase() || '';
    const pickupLabel = ShippingMethod.Pickup.toLowerCase();

    return (
      shippingMethodId.includes(pickupLabel) ||
      shippingMethodTitle.includes(pickupLabel)
    );
  });

  constructor(
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.initOrderDetails();
  }

  private isIsoTimestamp(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value);
  }

  private formatIsoTimestamp(isoString: string): string {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) {
      return isoString;
    }
    const formatted = this.datePipe.transform(date, 'EEEE, MMMM dd h:mm a');
    return formatted || isoString;
  }

  private formatDate2DeliveryDateLabel(
    date: Date | string = new Date()
  ): string {
    const DATE_FORMAT = 'EEEE, MMMM dd';

    const formatCurrentDate = this.datePipe.transform(date, DATE_FORMAT);
    if (!formatCurrentDate) {
      return 'As Soon As Possible';
    }

    return `${formatCurrentDate} asap`;
  }

  private initOrderDetails(): void {
    const orderId: string | null =
      this.activatedRoute.snapshot.paramMap.get('orderId');

    const order: WooCommerceCheckoutResponse = this.getOrderById(orderId)!;
    if (order == null) {
      this.router.navigate(['/']);
      return;
    }

    this.orderDetails.set(this.normalizeOrderDetails(order));
  }

  private getOrderById(
    orderId: string | null
  ): WooCommerceCheckoutResponse | undefined {
    if (orderId == null) {
      return;
    }

    const selectedOrder = this.store.selectSnapshot(OrdersState.getOrderById(orderId));
    if (selectedOrder) {
      return selectedOrder;
    }

    const stateOrders: WooCommerceCheckoutResponse[] =
      this.store.selectSnapshot((state: { orders: { items: WooCommerceCheckoutResponse[] } }) => {
        return state.orders?.items || [];
      });

    return stateOrders.find((candidate: any) => {
      const nestedOrderId = this.toNumber(candidate?.data?.order?.id);
      return nestedOrderId === this.toNumber(orderId);
    });
  }

  private normalizeOrderDetails(rawOrder: any): WooCommerceCheckoutResponse {
    if (rawOrder?.order_id && rawOrder?.products?.length >= 0 && rawOrder?.totals) {
      return this.normalizeAlreadyMapped(rawOrder);
    }

    const wrappedOrder = rawOrder?.data?.order || rawOrder?.order || null;
    const order = wrappedOrder || rawOrder || {};
    const orderTotals = order?.totals || {};
    const payment = rawOrder?.data?.payment || rawOrder?.payment || {};

    const shippingLine = order?.shipping_line || null;
    const shippingLines = Array.isArray(order?.shipping_lines)
      ? order.shipping_lines
      : shippingLine
        ? [shippingLine]
        : [];

    const tipAmount = orderTotals?.tip_amount ?? 0;
    const feeLines = Array.isArray(order?.fee_lines)
      ? order.fee_lines
      : Number(tipAmount) !== 0
        ? [{ total: this.toNumericString(tipAmount) }]
        : [];

    const totalTax = orderTotals?.total_tax ?? order?.total_tax ?? 0;
    const taxLines = Array.isArray(order?.tax_lines)
      ? order.tax_lines
      : Number(totalTax) !== 0
        ? [{ tax_total: this.toNumericString(totalTax) }]
        : [];

    const products = this.mapProducts(order?.items || rawOrder?.products || []);

    return {
      order_id: this.toNumber(rawOrder?.order_id ?? order?.id),
      status: String(order?.status || payment?.status || 'processing'),
      order_key: String(order?.order_number || order?.id || ''),
      order_number: String(order?.order_number || ''),
      customer_note: String(
        order?.notes?.customer_note ?? order?.customer_note ?? ''
      ),
      customer_id: this.toNumber(order?.customer_id || 0),
      fulfillment_type: order?.fulfillment_type || undefined,
      billing_address: this.mapAddress(order?.billing_address),
      shipping_address: this.mapAddress(order?.shipping_address),
      payment_method: String(
        order?.payment_method || payment?.gateway || ''
      ),
      payment_result: {
        payment_status: String(payment?.status || ''),
        payment_details: [
          {
            key: 'transaction_id',
            value: String(payment?.transaction_id || '')
          }
        ],
        redirect_url: ''
      },
      additional_fields: [],
      extensions: {},
      expected_delivery_date: String(
        order?.expected_delivery_date || order?.delivery_at || ''
      ),
      payment_method_title: String(
        order?.payment_method_title || order?.payment_method || ''
      ),
      products,
      refunds: [],
      totals: {
        discount_total: this.toNumericString(
          orderTotals?.discount_total ?? orderTotals?.coupon_amount ?? order?.discount_total ?? 0
        ),
        discount_tax: this.toNumericString(
          orderTotals?.discount_tax ?? order?.discount_tax ?? 0
        ),
        fee_lines: feeLines,
        coupon_lines: order?.coupon_lines || [],
        shipping_total: this.toNumericString(
          orderTotals?.shipping_total ?? order?.shipping_total ?? 0
        ),
        shipping_tax: this.toNumericString(
          orderTotals?.shipping_tax ?? order?.shipping_tax ?? 0
        ),
        shipping_lines: this.mapShippingLines(shippingLines),
        cart_tax: this.toNumericString(orderTotals?.cart_tax ?? order?.cart_tax ?? 0),
        tax_lines: taxLines,
        total: this.toNumericString(orderTotals?.total ?? order?.total ?? 0),
        total_tax: this.toNumericString(totalTax)
      }
    };
  }

  private normalizeAlreadyMapped(order: WooCommerceCheckoutResponse): WooCommerceCheckoutResponse {
    if (!order.expected_delivery_date) {
      order = { ...order, expected_delivery_date: new Date().toISOString() };
    }
    return order;
  }

  private mapProducts(products: any[]): WooCommerceCheckoutResponse['products'] {
    if (!Array.isArray(products)) {
      return [];
    }

    return products.map((item: any) => ({
      id: this.toNumber(item?.id || item?.product_id || 0),
      name: String(item?.name || ''),
      quantity: this.toNumber(item?.quantity || 0),
      subtotal: this.toNumericString(item?.subtotal ?? item?.total ?? 0),
      subtotal_tax: this.toNumericString(item?.subtotal_tax ?? item?.total_tax ?? 0),
      total: this.toNumericString(item?.total ?? item?.subtotal ?? 0),
      total_tax: this.toNumericString(item?.total_tax ?? item?.subtotal_tax ?? 0),
      item_meta: this.mapItemMeta(item),
      variant_name: item?.variant_name ?? null,
      variant_options: item?.variant_options ?? null
    }));
  }

  private mapItemMeta(item: any): { label: string; value: string }[] {
    if (Array.isArray(item?.item_meta)) {
      return item.item_meta;
    }

    if (!Array.isArray(item?.addons)) {
      return [];
    }

    return item.addons.map((addon: any) => ({
      label: String(addon?.name || addon?.title || ''),
      value: String(addon?.value ?? addon?.amount ?? '')
    }));
  }

  private mapShippingLines(shippingLines: any[]): any[] {
    if (!Array.isArray(shippingLines)) {
      return [];
    }

    return shippingLines.map((line: any) => ({
      id: this.toNumber(line?.id || 0),
      method_title: String(line?.method_title || line?.title || ''),
      method_id: String(line?.method_id || line?.method || line?.method_title || ''),
      instance_id: String(line?.instance_id || ''),
      total: this.toNumericString(line?.total ?? 0),
      total_tax: this.toNumericString(line?.total_tax ?? 0),
      taxes: line?.taxes || []
    }));
  }

  private mapAddress(address: any): WooCommerceCheckoutResponse['shipping_address'] {
    return {
      first_name: String(address?.first_name || ''),
      last_name: String(address?.last_name || ''),
      email: String(address?.email || ''),
      company: String(address?.company || ''),
      address_1: String(address?.address_1 || ''),
      address_2: String(address?.address_2 || ''),
      city: String(address?.city || ''),
      state: String(address?.state || address?.state_code || ''),
      postcode: String(address?.postcode || ''),
      country: String(address?.country || address?.country_code || ''),
      phone: String(address?.phone || '')
    };
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }

    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  private toNumericString(value: unknown): string {
    return `${this.toNumber(value)}`;
  }

  private getDeliveryDateValue(orderDetails: any): string {
    return String(orderDetails?.expected_delivery_date || '');
  }
}
